import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { AppStatusStage } from 'data/constants/application-types';
import * as S from '../../../Dropdowns.styled';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

type Props = {
    status: ClientApplicationStatus | null;
    assessmentAgreementSigned: boolean;
    visaOfferSent: boolean;
    visaAgreementSigned: boolean;
    dateFullyPaid: boolean;
    consultantName: string;
    open: boolean;
};

export enum StageIconType {
    IDLE = 'IDLE',
    IN_PROCESS = 'IN_PROCESS',
    FAIL = 'FAIL',
    DONE = 'DONE',
}

export type StepperStageType = {
    name: AppStatusStage | string;
    iconType: StageIconType;
};

const Stepper = ({
    status,
    assessmentAgreementSigned,
    visaOfferSent,
    visaAgreementSigned,
    dateFullyPaid,
    consultantName,
    open,
}: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    const getAssignedStatusStep = () => {
        if (consultantName === '') {
            return {
                name: `${t('waitingToBeAssigned', { lng: language })}`,
                iconType: StageIconType.IN_PROCESS,
            };
        } else {
            return {
                name: `${t('assignedTo', { lng: language })} ${consultantName}`,
                iconType: StageIconType.DONE,
            };
        }
    };

    const getAssessmentAgreementStep = () => {
        if (consultantName === '') {
            return {
                name: `${t('assessmentAgreement', { lng: language })}`,
                iconType: StageIconType.IDLE,
            };
        }
        if (assessmentAgreementSigned) {
            return {
                name: `${t('assessmentAgreementSigned', { lng: language })}`,
                iconType: StageIconType.DONE,
            };
        }
        return {
            name: `${t('assessmentAgreementPending', { lng: language })}`,
            iconType: StageIconType.IN_PROCESS,
        };
    };

    const getVisaAgreementStep = () => {
        if (!visaOfferSent) {
            return {
                name: `${t('visaAgreement', { lng: language })}`,
                iconType: StageIconType.IDLE,
            };
        }
        if (visaAgreementSigned) {
            return {
                name: `${t('visaAgreementSigned', { lng: language })}`,
                iconType: StageIconType.DONE,
            };
        }
        return {
            name: `${t('visaAgreementPending', { lng: language })}`,
            iconType: StageIconType.IN_PROCESS,
        };
    };

    const getPaymentStep = () => {
        if (!visaOfferSent) {
            return {
                name: `${t('visaPayment', { lng: language })}`,
                iconType: StageIconType.IDLE,
            };
        }
        if (dateFullyPaid) {
            return {
                name: `${t('visaPaymentComplete', { lng: language })}`,
                iconType: StageIconType.DONE,
            };
        }
        return {
            name: `${t('visaPaymentPending', { lng: language })}`,
            iconType: StageIconType.IN_PROCESS,
        };
    };

    const getDocumentsStep = () => {
        if (
            status === ClientApplicationStatus.CLIENT_WITH_ISSUES ||
            status === ClientApplicationStatus.PROCESSING ||
            status === ClientApplicationStatus.NOT_ASSIGNED
        ) {
            return {
                name: `${t('documentsPending', { lng: language })}`,
                iconType: StageIconType.IN_PROCESS,
            };
        }
        if (status === ClientApplicationStatus.IN_REVIEW) {
            return {
                name: `${t('documentsInReview', { lng: language })}`,
                iconType: StageIconType.IN_PROCESS,
            };
        }

        if (
            status === ClientApplicationStatus.READY_FOR_SUBMISSION ||
            status === ClientApplicationStatus.SUBMITTED ||
            status === ClientApplicationStatus.AWAITING_ITA ||
            status === ClientApplicationStatus.AWAITING_LOA ||
            status === ClientApplicationStatus.VISA_RESULTS
        ) {
            return {
                name: `${t('documentsAccepted', { lng: language })}`,
                iconType: StageIconType.DONE,
            };
        }

        return {
            name: `${t('documents', { lng: language })}`,
            iconType: StageIconType.IDLE,
        };
    };

    const getResultsStep = () => {
        if (status === ClientApplicationStatus.READY_FOR_SUBMISSION) {
            return {
                name: `${t('readyForSubmission', { lng: language })}`,
                iconType: StageIconType.IN_PROCESS,
            };
        }
        if (status === ClientApplicationStatus.AWAITING_ITA) {
            return {
                name: `${t('awaitingInvitationToApply', { lng: language })}`,
                iconType: StageIconType.IN_PROCESS,
            };
        }
        if (status === ClientApplicationStatus.AWAITING_LOA) {
            return {
                name: `${t('awaitingLetterOfAcceptance', { lng: language })}`,
                iconType: StageIconType.IN_PROCESS,
            };
        }
        if (status === ClientApplicationStatus.SUBMITTED) {
            return {
                name: `${t('applicationSubmitted', { lng: language })}`,
                iconType: StageIconType.DONE,
            };
        }
        if (status === ClientApplicationStatus.VISA_RESULTS) {
            return {
                name: `${t('visaResultsReceived', { lng: language })}`,
                iconType: StageIconType.DONE,
            };
        }
        return {
            name: `${t('applicationSubmission', { lng: language })}`,
            iconType: StageIconType.IDLE,
        };
    };

    const assignedStatusStep = getAssignedStatusStep();
    const assessmentAgreementStep = getAssessmentAgreementStep();
    const visaAgreementStep = getVisaAgreementStep();
    const visaPaymentStep = getPaymentStep();
    const documentsStep = getDocumentsStep();
    const resultsStep = getResultsStep();

    function getPreparedStages(): StepperStageType[] {
        return [
            assignedStatusStep,
            assessmentAgreementStep,
            visaAgreementStep,
            visaPaymentStep,
            documentsStep,
            resultsStep,
        ];
    }

    const getStageIcon = (iconType: StageIconType) => {
        switch (iconType) {
            case StageIconType.IDLE:
                return (
                    <S.StageIconIdle>
                        <S.Idle />
                    </S.StageIconIdle>
                );
            case StageIconType.DONE:
                return (
                    <S.StageIcon>
                        <S.Check />
                    </S.StageIcon>
                );
            case StageIconType.IN_PROCESS:
                return (
                    <S.StageIconInProcess>
                        <S.Hourglass />
                    </S.StageIconInProcess>
                );
            case StageIconType.FAIL:
                return (
                    <S.StageIconDanger>
                        <S.Fail />
                    </S.StageIconDanger>
                );
        }
    };

    const stages = getPreparedStages();

    return (
        <S.StatusContainer open={open}>
            <div style={{ margin: '18px 32px' }}>
                <S.StatusTitle>Visa Status</S.StatusTitle>
                <S.Stepper>
                    {stages &&
                        stages.map((stage, i) => {
                            const lastItem = i === stages.length - 1;
                            return (
                                <S.Stage key={i}>
                                    <S.IconContainer>
                                        {getStageIcon(stage.iconType)}
                                        {!lastItem && <S.StatusLine />}
                                    </S.IconContainer>

                                    <p style={{ marginTop: '8px' }}>{stage.name}</p>
                                </S.Stage>
                            );
                        })}
                </S.Stepper>
            </div>
        </S.StatusContainer>
    );
};

export default Stepper;
